<template>
  <div>
    <div class="cta-wrapper">
      <p class="headline">{{ $t('HOUSE_BOARD_NOTIFICATION_TITLE') }}</p>
      <sh-button
        v-if="hasMoreNotifications"
        naked="true"
        @click="handleSeeMoreNotifications"
      >{{ $t('HOUSE_BOARD_NOTIFICATION_SEE_ALL_CTA') }}</sh-button>
    </div>

    <notification-list
      :isSlideable="$device.isMobileOrTablet"
      :notifications="lastThreeNotifications"
    />

    <div class="local-house-link">
      <nuxt-link
        class="headline"
        :to="localePath({name: 'house-slug', params: {slug: localHouse.slug}})"
      >{{ localHouse.name }} &gt;</nuxt-link>
    </div>

    <div class="details spaced">
      <div>
        <p>{{ $t('HOUSE_BOARD_HOUSE_OPEN_STATUS') }}</p>
        <p class="description">{{ openingHour }} - {{ closingHour }}</p>
      </div>
      <sh-button
        outline="true"
        color="secondary"
        :to="localePath({ name: 'settings-membership' })"
      >{{ $t('HOUSE_BOARD_MEMBERSHIP_CARD_LABEL') }}</sh-button>
    </div>

    <div class="spaced">
      <sh-button
        class="room-cta"
        color="secondary"
        size="medium"
        fullWidth="true"
        :to="localePath({ path: '/bedrooms' })"
      >{{ $t('HOUSE_BOARD_BOOK_ROOM_CTA') }}</sh-button>
    </div>

    <div class="house-details">
      <nuxt-link
        class="house-links"
        :to="localePath({ name: 'contact' })"
      >{{ $t('HOUSE_BOARD_CONTACT_HOUSE') }}</nuxt-link>
      <nuxt-link
        class="house-links"
        :to="localePath({ name: 'house-slug-rules', params: { slug: localHouse.slug } })"
      >{{ $t('HOUSE_BOARD_HOUSE_RULES') }}</nuxt-link>
    </div>
  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import { ShBlade } from '@sohohouse/design-system'
  import { decorators } from '@sohohouse/platform-resources'

  import NotificationList from '~/components/notification-list'
  import UIMixin from '~/mixins/ui'
  import { VIEWS } from '~/constants/house-board-views'

  const { mapGetters: mapAccountGetters } = createNamespacedHelpers('user/account')
  const { mapActions: mapModalsActions } = createNamespacedHelpers('ui/modals')
  const {
    mapGetters: mapNotificationGetters,
    mapActions: mapNotificationActions
  } = createNamespacedHelpers('notifications')

  const { DateTime } = decorators.shared

  export default {
    components: {
      ShBlade,
      NotificationList
    },

    mixins: [
      UIMixin
    ],

    computed: {
      ...mapAccountGetters(['localHouse']),
      ...mapNotificationGetters(['unreadNotifications', 'showNotificationAlert']),

      openingHour () {
        const { opensAt } = this.localHouse.operatingHours || {}
        return this.dateToHour(opensAt)
      },

      closingHour () {
        const { closesAt } = this.localHouse.operatingHours || {}
        return this.dateToHour(closesAt)
      },

      lastThreeNotifications () {
        return this.unreadNotifications.slice(0, 3)
      },

      hasMoreNotifications () {
        return this.unreadNotifications.length > 3
      }
    },

    mounted () {
      this.setUnseenNotifications()
    },

    watch: {
      showNotificationAlert: 'setUnseenNotifications'
    },

    methods: {
      ...mapModalsActions(['close']),
      ...mapNotificationActions(['markNotificationsAsSeen']),

      dateToHour (t) {
        const d = new DateTime(t)

        return this.$d(d.withOffset.epoch, 'time')
      },

      closeHouseBoard () {
        this.$ui.modal.close(this.name)
      },

      handleSeeMoreNotifications () {
        this.$emit('handleViewChange', VIEWS.NOTIFICATION)
      },

      setUnseenNotifications () {
        if (
          this.showNotificationAlert
          && this.unreadNotifications
          && this.unreadNotifications.length > 0
        ) {
          const [notification] = this.unreadNotifications
          this.markNotificationsAsSeen(notification.notificationGroup)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
.board-wrapper {
  padding: $space-050;

  @include tablet {
    padding: $space-050 $space-060;
  }

  .local-house-link {
    margin: $space-070 0;
  }

  .spaced {
    margin-bottom: $space-070;
  }

  .welcome {
    margin-top: $space-010;
    margin-bottom: $space-060;
  }

  .details {
    display: flex;
    justify-content: space-between;

    @include mobile-sm {
      display: block;
      margin-bottom: $space-050;

      div {
        margin-bottom: $space-050;
      }

      a {
        width: 100%;
      }
    }
  }

  .house-details {
    a {
      display: block;
    }

    :first-child {
      margin-bottom: $space-070;
    }
  }
}

p,
a:not(.room-cta) {
  @include font("040");
  color: $color-white;

  &.description {
    color: $color-gray_5;
  }

  &.headline {
    @include font("060");

    color: $color-white;
  }

  &.house-links {
    @include fontBold("040");
  }
}

.cta-wrapper {
  @include font("040");

  display: flex;
  justify-content: space-between;
  margin-bottom: $space-030;

  button {
    color: white;
  }
}
</style>
